import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from 'utils/DateUtils';

export default function ProjectProgressChart({
  total = 0,
  draft = 0,
  validated = 0,
  delivered = 0,
  online = 0,
  deadline = null,
}) {
  const { t } = useTranslation();

  const statuses = [
    {
      label: t('Brouillons'),
      value: draft,
      color: '#CFCFCF',
      x: 30
    },
    {
      label: t('Livrés'),
      value: delivered,
      color: '#FFD44A',
      x: 90
    },
    {
      label: t('Validés'),
      value: validated,
      color: '#3FBF9A',
      x: 150
    },
    {
      label: t('En ligne'),
      value: online,
      color: '#8180F9',
      x: 210
    },
  ];

  const maxValue = Math.max(draft, validated, delivered, online, 1);
  const chartHeight = 100;
  const chartTop = 20;

  const points = statuses.map((status) => ({
    x: status.x,
    y: chartTop + chartHeight - (status.value / maxValue) * chartHeight,
    value: status.value,
    color: status.color,
  }));

  const linePath = points
    .map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  const safeTotal = total || 0;
  const deliveredPercent =
    safeTotal > 0 ? Math.round((delivered / safeTotal) * 100) : 0;

  const deadlineLabel = deadline
    ? formatDateShort(deadline)
    : '';

  return (
    <div
      style={{
        borderRadius: 10,
        border: '1px solid #F3F4F6',
        padding: '10px 12px 12px',
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Légende */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 12,
          fontSize: 12,
          flexWrap: 'wrap',
        }}
      >
        {statuses.map((status, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: status.color,
                display: 'inline-block',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span className="project-progress-chart-status-label" style={{ color: '#4B5563' }}>{status.label}</span>
              <span className="project-progress-chart-status-value-label" style={{ color: '#111827' }}>
                {status.value}
                {status.label === t('Livrés')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Graphique SVG */}
      <div style={{ width: '100%', height: 160 }}>
        <svg width="100%" height="160" viewBox="0 0 260 160">
          {/* Grille verticale */}
          {statuses.map((status) => (
            <line
              key={status.x}
              x1={status.x}
              y1={chartTop}
              x2={status.x}
              y2={chartTop + chartHeight + 10}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
          ))}

          {/* Ligne rouge de deadline */}
          <line
            x1={245}
            y1={chartTop}
            x2={245}
            y2={chartTop + chartHeight + 10}
            stroke="#FB5A3A"
            strokeWidth="2"
          />

          {/* Ligne de progression */}
          <path
            d={linePath}
            fill="none"
            stroke="#6366F1"
            strokeWidth="2"
          />

          {/* Points avec valeurs */}
          {points.map((p, idx) => (
            <g key={idx}>
              <circle
                cx={p.x}
                cy={p.y}
                r={5}
                fill={statuses[idx].color}
                stroke="#FFFFFF"
                strokeWidth="2"
              />

              {p.value > 0 && (
                <text
                  x={p.x}
                  y={p.y - 12}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="#111827"
                >
                  {p.value}
                </text>
              )}
            </g>
          ))}


        </svg>
      </div>


      <div
        style={{
          marginTop: -10,
          fontSize: 11,
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 10px',
        }}
      >
        <span className="project-progress-chart-total-label" style={{ color: '#6B7280', fontWeight: 500 }}>
          {t('Total')} ({safeTotal})
        </span>

        {deadlineLabel && (
          <span className="project-progress-chart-deadline-label" style={{ color: '#FB5A3A' }}>
            {deadlineLabel}
          </span>
        )}
      </div>
    </div>
  );
}